import { Icon, usePrompt } from '@ur-solutions/react-components'
import { useTranslate } from '@ur-solutions/react-hooks'
import { Card } from '@/components_deprecated'
import { parseISO } from 'date-fns'
import { format } from '@/utils/date-fns'
import styled from 'styled-components'
import { useState } from 'react'
import type { WorkPeriodNode } from '../types.graphql'
import { DietButton } from '../components/DietButton'
import { Button, H3 } from '@/components'
import { ViewWorkPeriodLogsModal } from '../components/view-work-period-logs-modal'
import { AddonButton } from '@/modules/activities/components/overview/AddonCell'
import type { SalaryModuleConfiguration } from '../hooks/useSalaryModuleConfiguration'
import { SalaryDayButton } from '../components/SalaryDayButton'
import { AddonOverview } from '../AddonOverview'
import { WorkPeriodApprovalBoxes } from './WorkPeriodApprovalBoxes'
import { WorkPeriodTimeBlocks } from './WorkPeriodTimeBlocks'
import { WorkPeriodHoursAndMap } from '../WorkPeriodHoursAndMap'
import { SalarySettingSelect } from '@/components_deprecated/Select/SalarySettingSelect'
import { useSalaryApproveColumns } from '../hooks/useSalaryApproveColumns'
import { ColumnNames } from '@/modules/dynamic_tables'

const WorkPeriodContainer = styled(Card)`
  display: flex;
  flex-direction: column;
  gap: 1rem;
  margin: 1rem 0;
  width: 100%;

  & > h3 {
    margin: 0;
  }

  & > div > span {
    opacity: 0;
  }
  &:hover {
    & > div > span {
      opacity: 1;
    }
  }
`

const WorkPeriodWrapper = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  width: 100%;

  p {
    font-weight: 600;
  }

  ${props => props.theme.media.mobile} {
    width: 100%;
    max-width: 100%;
  }
`

const ActionWrapper = styled.div`
  color: ${props => props.theme.colors.gray1};
  background-color: ${props => props.theme.colors.gray8};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  font-size: 18px;
  display: flex;
  justify-content: space-between;
  overflow: hidden;

  & > div {
    width: 100%;
    padding: 0.5rem 1rem;
  }
  & > div:hover {
    background-color: ${props => props.theme.colors.gray7};
    cursor: pointer;
  }
`

const FlexWrapper = styled.div`
  display: flex;
  flex-direction: column;
  margin-top: 0;
  gap: 0.5rem;

  ${props => props.theme.media.mobile} {
    width: 100%;
    flex-direction: column;
    width: 100%;
  }
`

const FlexRow = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  gap: 0.5rem 1rem;
  align-items: center;
`

const WorkPeriodTitle = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.75rem;

  & > h3 {
    margin: 0;
  }

  ${props => props.theme.media.mobile} {
    flex-direction: column;
    align-items: flex-start;
    gap: 0.5rem;
  }
`

// Aligns the salary-setting combobox with the 44px action buttons by removing
// the combobox wrapper's default bottom margin.
const InputCell = styled.div`
  display: flex;
  align-items: center;

  & .mb-2 {
    margin-bottom: 0;
  }
`

const HeaderColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 1rem;
`

const HeaderTopRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 0.75rem 1rem;
  align-items: center;
  justify-content: space-between;
  ${props => props.theme.media.mobile} {
    h3 {
      font-size: 1rem;
    }
  }
`

const HeaderActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 0.5rem 0.75rem;
`

const ControlsCard = styled.div`
  display: flex;
  padding: 0.75rem;
  border: 1px solid ${props => props.theme.colors.gray9};
  border-radius: ${props => props.theme.sizes.defaultBorderRadius};
  background: ${props => props.theme.colors.white};
`

const EditedAfterLockNote = styled.div`
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  white-space: nowrap;
  color: ${props => props.theme.colors.orange};
  font-size: 0.85rem;
  font-weight: 600;
`

const TextAndAction = styled.div<{ border?: boolean; disabled?: boolean }>`
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 0 1rem;
  border-radius: ${props => props.theme.standardBorderRadius};
  border: ${props =>
    props.border ? `1px solid ${props.theme.colors.gray9}` : 'none'};
  background-color: ${props => props.theme.colors.white};
  opacity: ${props => (props.disabled ? 0.5 : 1)};
  cursor: ${props => (props.disabled ? 'not-allowed' : 'inherit')};

  ${props =>
    props.disabled &&
    `
    & * {
      pointer-events: none;
      cursor: not-allowed;
    }
  `}

  & > *:first-child {
    border-right: 1px solid ${props => props.theme.colors.gray9};
    margin-right: 1rem;
    padding-right: 1rem;
  }
  & > *:last-child {
    cursor: pointer;
  }
  ${props => props.theme.media.mobile} {
    width: 100%;
  }
`

export const WorkPeriodDetails: React.FC<{
  workPeriod: WorkPeriodNode
  userId: string
  salaryCalendarSettings: SalaryModuleConfiguration
  // Whether the current user may edit this work period's salary data
  // (salary setting, salary day, diet, addons). Approval visibility/actions
  // are governed separately by the salary config and per-user approval rules.
  canEdit: boolean
  patchWorkPeriod: (
    workPeriodId: string,
    values: Partial<
      Pick<
        WorkPeriodNode,
        | 'approvedByUser'
        | 'approvedByManager'
        | 'userComment'
        | 'managerComment'
      >
    > &
      Partial<{ calendarItem: string | null; salarySetting: string | null }>
  ) => void
  onClose?: () => void
  refetch: () => void
}> = ({
  workPeriod,
  userId,
  salaryCalendarSettings,
  canEdit,
  patchWorkPeriod,
  onClose,
  refetch,
}) => {
  const translations = useTranslate({
    workPeriod: 'common.work-period',
    addons: 'salary.addons',
    actionLog: 'salary.work-period-log',
    editedAfterLock: 'salary.edited-after-lock',
    editedAfterLockAt: ['salary.edited-after-lock-at', { date: '' }],
  })

  const addPrompt = usePrompt()
  const openLog = () =>
    addPrompt<boolean | null>(resolve => (
      <ViewWorkPeriodLogsModal workPeriodId={workPeriod.id} onClose={resolve} />
    ))

  const [showMap, setShowMap] = useState(true)

  const workPeriodStart = parseISO(workPeriod.datetimeStart)
  const workPeriodEnd = parseISO(workPeriod.datetimeEnd)

  const displayDateDays = `${format(workPeriodStart, 'd MMM HH:mm')} - ${format(
    workPeriodEnd,
    'd MMM HH:mm'
  )}`

  const title = `${translations.workPeriod}: ${displayDateDays}`

  const editedAfterLockText = workPeriod.lockedAt
    ? translations.editedAfterLockAt({
        date: format(parseISO(workPeriod.lockedAt), 'dd.MM.yyyy HH:mm'),
      })
    : translations.editedAfterLock

  const showApproveWrapper =
    salaryCalendarSettings.driverApprove ||
    salaryCalendarSettings.managerApprove

  const activitiesOnly = workPeriod.salarySetting
    ? workPeriod.salarySetting.salaryType !== 'HOUR' &&
      (workPeriod.salaryDay?.partOfDay ?? 0) > 0
    : false

  // Respect the same dynamic-table column settings as the approve table/cards.
  const { isEnabled: isColumnEnabled } = useSalaryApproveColumns()
  const showSalaryMode = isColumnEnabled(ColumnNames.SALARY_NAME)
  const showDiet = isColumnEnabled(ColumnNames.DIET)
  const showAddons = isColumnEnabled(ColumnNames.ADDONS)
  const showSalaryDay =
    showSalaryMode && workPeriod.salarySetting?.salaryType === 'DAY'
  const hasControls = showSalaryMode || showDiet || showAddons

  return (
    <FlexWrapper>
      {onClose && (
        <HeaderColumn>
          <HeaderTopRow>
            <FlexRow>
              <ActionWrapper>
                <div onClick={() => onClose()}>
                  <Icon icon="arrow-left" onClick={() => onClose()} />
                </div>
              </ActionWrapper>
              <H3>{title}</H3>
            </FlexRow>
            <HeaderActions>
              {workPeriod.editedAfterLock && (
                <EditedAfterLockNote>
                  <Icon icon="exclamation-triangle" color="orange" />
                  {editedAfterLockText}
                </EditedAfterLockNote>
              )}
              <Button
                size="icon-lg"
                variant="outline"
                title={translations.actionLog}
                aria-label={translations.actionLog}
                onClick={openLog}
              >
                <Icon icon="clock-rotate-left" />
              </Button>
            </HeaderActions>
          </HeaderTopRow>
          {hasControls && (
            <ControlsCard>
              <WorkPeriodTitle>
                {showSalaryMode && (
                  <InputCell>
                    <SalarySettingSelect
                      value={workPeriod.salarySetting?.id || null}
                      disabled={workPeriod.approvedByManager || !canEdit}
                      className="bg-gray-100 border-gray-200"
                      onChange={value =>
                        patchWorkPeriod(workPeriod.id, {
                          salarySetting: value === '' ? null : value,
                        })
                      }
                    />
                  </InputCell>
                )}
                {showSalaryDay && (
                  <SalaryDayButton
                    salaryDay={workPeriod.salaryDay}
                    workPeriod={workPeriod}
                    userId={userId}
                    canEdit={!workPeriod.approvedByManager && canEdit}
                    border={true}
                    onChange={() => refetch()}
                  />
                )}
                {showDiet && (
                  <DietButton
                    workPeriod={workPeriod}
                    userId={userId}
                    canEdit={!workPeriod.approvedByManager && canEdit}
                    border={true}
                    onChange={() => refetch()}
                  />
                )}
                {showAddons && (
                  <TextAndAction
                    border={true}
                    disabled={workPeriod.approvedByManager || !canEdit}
                    onClick={() => {}}
                  >
                    <div>{translations.addons}</div>
                    <AddonButton
                      userId={userId}
                      datetimeStart={workPeriodStart}
                      datetimeEnd={workPeriodEnd}
                      addonsAsText={['']}
                      disabled={workPeriod.approvedByManager || !canEdit}
                    />
                  </TextAndAction>
                )}
              </WorkPeriodTitle>
            </ControlsCard>
          )}
        </HeaderColumn>
      )}

      <WorkPeriodWrapper>
        <WorkPeriodContainer key={workPeriod.id}>
          {!onClose && (
            <HeaderColumn>
              <HeaderTopRow>
                <FlexRow>
                  <H3>{title}</H3>
                </FlexRow>
                <HeaderActions>
                  {workPeriod.editedAfterLock && (
                    <EditedAfterLockNote>
                      <Icon icon="exclamation-triangle" color="orange" />
                      {editedAfterLockText}
                    </EditedAfterLockNote>
                  )}
                  <Button
                    size="icon-lg"
                    variant="outline"
                    title={translations.actionLog}
                    aria-label={translations.actionLog}
                    onClick={openLog}
                  >
                    <Icon icon="clock-rotate-left" />
                  </Button>
                </HeaderActions>
              </HeaderTopRow>
              {hasControls && (
                <ControlsCard>
                  <WorkPeriodTitle>
                    {showSalaryMode && (
                      <InputCell>
                        <SalarySettingSelect
                          value={workPeriod.salarySetting?.id || null}
                          disabled={workPeriod.approvedByManager || !canEdit}
                          className="bg-gray-100 border-gray-200"
                          onChange={value =>
                            patchWorkPeriod(workPeriod.id, {
                              salarySetting: value === '' ? null : value,
                            })
                          }
                        />
                      </InputCell>
                    )}
                    {showDiet && (
                      <DietButton
                        workPeriod={workPeriod}
                        userId={userId}
                        canEdit={!workPeriod.approvedByManager && canEdit}
                        border={true}
                      />
                    )}
                    {showAddons && (
                      <TextAndAction
                        border={true}
                        disabled={workPeriod.approvedByManager || !canEdit}
                        onClick={() => {}}
                      >
                        <div>{translations.addons}</div>
                        <AddonButton
                          userId={userId}
                          datetimeStart={workPeriodStart}
                          datetimeEnd={workPeriodEnd}
                          addonsAsText={['']}
                          disabled={workPeriod.approvedByManager || !canEdit}
                        />
                      </TextAndAction>
                    )}
                  </WorkPeriodTitle>
                </ControlsCard>
              )}
            </HeaderColumn>
          )}
          <WorkPeriodTimeBlocks
            datetimeStart={workPeriod.datetimeStart}
            datetimeEnd={workPeriod.datetimeEnd}
            startLocation={workPeriod.startLocation?.details}
            endLocation={workPeriod.endLocation?.details}
            timeEntries={workPeriod.timeEntries}
          />
          {showApproveWrapper && (
            <WorkPeriodApprovalBoxes
              workPeriod={workPeriod}
              patchWorkPeriod={patchWorkPeriod}
              driverApprove={salaryCalendarSettings.driverApprove}
              managerApprove={salaryCalendarSettings.managerApprove}
            />
          )}
          <WorkPeriodHoursAndMap
            workPeriodId={workPeriod.id}
            activitiesOnly={activitiesOnly}
            showMap={showMap}
            onToggleMap={() => setShowMap(value => !value)}
          />
        </WorkPeriodContainer>
      </WorkPeriodWrapper>

      <AddonOverview
        userId={workPeriod.user.id}
        datetimeEnd={workPeriodEnd}
        datetimeStart={workPeriodStart}
      />
    </FlexWrapper>
  )
}
